<template>
  <div class="container-fluid d-flex flex-column vh-100 p-4">
    <div
      v-if="isLoading"
      class="text-center mt-5"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">
        Loading preview data...
      </p>
    </div>

    <div
      v-else-if="error"
      class="text-center p-5"
    >
      <div class="alert alert-danger">
        {{ error }}
      </div>
    </div>

    <div
      v-else-if="previews.length"
      class="row flex-fill overflow-hidden mx-0"
    >
      <div class="col-md-9 px-3 mt-3 overflow-auto h-100">
        <div>
          <div class="card custom-block mt-3">
            <div class="card-body d-flex justify-content-between">
              <div class="align-items-center">
                <h3 class="card-title">
                  Name: {{ creative.name }}
                </h3>
                <h4 class="card-subtitle mb-2 text-muted">
                  Status: <span :class="walmart.getStatusBadge(creative.status)">
                    {{ creative.status }}
                  </span>
                </h4>
              </div>
            </div>
          </div>
          <div>
            <div
              v-for="(preview, index) in previews"
              :key="index"
              class="mt-3 mb-2 border rounded custom-block p-3"
            >
              <h3>{{ adUnitTypes[preview.name] }}</h3>
              <img
                :src="preview.image"
                :alt="preview.name || 'Preview'"
                class="img-fluid"
              >
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-3 d-flex flex-column px-3 mt-3 overflow-auto h-100">
        <div class="card custom-block mt-3">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <h4 class="card-title">
                Actions
              </h4>
              <a
                v-if="!userId"
                href="/login"
                class="text-right d-inline-block login-proof"
              >
                Login
              </a>
            </div>
            <div class="mt-2">
              <button
                class="btn btn-outline-secondary btn-block"
                @click="copyShareLink"
              >
                <i class="fas fa-link mr-1" />
                Copy Share Link
              </button>
            </div>
            <div
              v-if="creative.layout_link && userId"
              class="mt-2"
            >
              <a
                class="btn btn-outline-info btn-block d-flex align-items-center justify-content-center"
                :href="creative.layout_link"
                target="_blank"
                rel="noopener"
              >
                <i class="fas fa-external-link-alt mr-1" />
                Layout
              </a>
            </div>
            <div
              v-if="userId"
              class="mt-2"
            >
              <button
                class="btn btn-primary btn-block"
                :disabled="!!showCommentBox"
                @click="createComment"
              >
                <i class="fas fa-comment mr-1" />
                Comment
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="userId"
          class="mt-3"
        >
          <b-form-checkbox
            v-model="showAllComments"
            class="mb-2"
            switch
          >
            Show all comments
          </b-form-checkbox>
          <CommentInput
            ref="commentInput"
            :comments-collection="comments"
            :edited-comment="editedComment"
          />
          <div class="comment-lists">
            <CommentItem
              v-for="comment in comments"
              :key="comment.id"
              :comment="comment"
              @commentEditorOpen="comment => {
                editedComment = comment.comment;
              }"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="text-center p-5"
    >
      <div class="alert alert-warning">
        No previews available for this creative.
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import * as walmart from '@frontend/components/helpers/walmart-helpers.js';
import { adUnitTypes } from '@frontend/constants/walmart-constants.js';
import CommentInput from '../common/share/CommentInput.vue';
import CommentItem from '../common/share/CommentItem.vue';

export default {
  name: 'WalmartPreviewShare',
  components: {
    CommentInput,
    CommentItem,
  },
  props: {
    token: {
      type: String,
      required: true
    },
    userId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      walmart,
      adUnitTypes,
      previews: [],
      creative: {},
      isLoading: true,
      error: null,
      showAllComments: false,
      commentId: null,
      editedComment: null,
    }
  },
  computed: {
    ...mapGetters('project', [
      'showCommentBox',
      'commentText',
      'commentStatus',
      'commentAttachments',
      'commentEditing',
      'commentReplying',
    ]),
    ...mapGetters('media', {
      commentList: 'historiesProjectComments',
      commentsSelected: 'historiesProjectCommentsSelected',
    }),
    comments() {
      return this.commentList;
    },
  },
  async mounted() {
    await this.fetchData();
    await this.getCommentList();
    this.$root.$on('close-comment', this.closeCommentBox);
    this.$root.$on('submit-comment', this.addComment);
  },
  beforeDestroy() {
    this.$root.$off('close-comment', this.closeCommentBox);
    this.$root.$off('submit-comment', this.addComment);
  },
  methods: {
    ...mapActions("media", [
      "addHistoriesProjectComment",
      'updateHistoriesProjectComment',
      "getHistoriesProjectComments",
      "deleteHistoriesProjectComment",
      "resolveHistoriesProjectComment",
    ]),
    ...mapActions('project', [
      'updateCommentBox',
      'updateCommentText',
      'updateCommentStatus',
      'updateCommentEditing',
      'updateCommentReplying',
    ]),
    async fetchData() {
      try {
        this.isLoading = true;
        this.error = null;

        const { data } = await axios.post('/walmart/fetch-shared-preview', {
          token: this.token
        });

        if (data.status === 'error') {
          this.error = data.message || 'Failed to load preview data';
          return;
        }

        this.previews = data.previews || [];
        this.creative = data.creative || {};

      } catch (error) {
        console.error('Error fetching preview data:', error);
        this.error = 'An error occurred while loading the preview data';
      } finally {
        this.isLoading = false;
      }
    },

    copyShareLink() {
      const shareLink = window.location.href;
      navigator.clipboard.writeText(shareLink);

      toastr.success('Share link copied');
    },

    async getCommentList() {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await this.getHistoriesProjectComments({
        comment_type: 6,
        type_id: this.creative.rapid_id,
        timezone: timezone,
      });
    },

    createComment() {
      this.$nextTick(() => {
        this.updateCommentBox(true);
      });
    },

    async addComment() {
      if (!this.commentText || !this.showCommentBox) return;
      await this.pushComment();
    },

    async pushComment() {
      try {
        const formData = new FormData();
        formData.append('comment_type', 6);
        formData.append('type_id', this.creative.rapid_id);
        formData.append('comment', this.commentText);
        formData.append('user_id', this.userId);
        if (this.commentReplying) {
          formData.append('parent_id', this.commentReplying);
        }
        if (this.commentStatus) {
          formData.append('status', this.commentStatus);
        }
        this.commentAttachments.forEach(file => formData.append('attachments[]', file));

        if (this.commentEditing) {
          await this.updateHistoriesProjectComment({ data: formData, id: this.commentEditing });
        } else {
          await this.addHistoriesProjectComment(formData);
        }
      } catch (err) {
        console.log("[err]", err);
      } finally {
        await this.getCommentList();
        this.closeCommentBox();
      }
    },

    closeCommentBox() {
      this.updateCommentText('');
      this.updateCommentStatus(null);
      this.updateCommentBox(false);
      this.updateCommentEditing(null);
      this.updateCommentReplying(null);
    }
  }
}
</script>

<style scoped>
.custom-block {
  background-color: #f8fafc;
}

.login-proof {
  text-decoration: none;
  color: #007bff;
}

.login-proof:hover {
  text-decoration: underline;
}
</style>
